<template>
  <div>
    <u-popover placement="bottom" trigger="click" :disabled="disabled">
      <template #content>
        <div class="avatar-selector">
          <div class="avatar-tabs">
            <div
              v-for="tab in tabs"
              :key="tab.value"
              class="avatar-tab"
              :class="{ active: activeTab === tab.value }"
              @click.stop="activeTab = tab.value"
            >
              {{ tab.label }}
            </div>
          </div>
          <div class="avatar-grid-wrap">
            <div class="avatar-grid-container">
              <div class="avatar-grid">
                <div
                  v-for="n in filteredAvatars"
                  :key="n"
                  class="avatar-item"
                  :class="{ selected: currentIcon === n }"
                  @click.stop="selectAvatar(n)"
                >
                  <img :src="`/assets/img/avatar/${n}.png`" alt="avatar" />
                  <div
                    v-if="activeTab === currentIconType && currentIcon === n"
                    class="selected-badge"
                  >
                    <BotIcon name="v_icon" :size="14" :padding="0" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div>
        <AgentAvatar
          :icon="currentIcon"
          :show-edit-mask="showEditMask"
          @click="clickSelectedAvatar(currentIconType)"
        />
      </div>
    </u-popover>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, computed } from 'vue';
import { generateRange } from '@/libs/tools';
import AgentAvatar from './index.vue';

export default defineComponent({
  name: 'AgentAvatarPicker',
  components: {
    AgentAvatar
  },
  props: {
    type: {
      type: String,
      default: 'humanoid'
    },
    icon: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showEditMask: {
      type: Boolean,
      default: false
    }
  },
  emits: ['select'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const getTypeFromIcon = num => {
      if (num <= 54) {
        return 'humanoid';
      }
      if (num >= 55 && num <= 58) {
        return 'robot';
      }
      if (num >= 59 && num <= 70) {
        return 'tool';
      }
      return 'humanoid';
    };
    const currentIcon = ref(props.icon);
    const currentIconType = ref(getTypeFromIcon(currentIcon.value));
    const activeTab = ref(getTypeFromIcon(currentIcon.value));

    const tabs = [
      {
        label: t(
          'components.AgentAvatar.picker.91d3506174913fbe1a78a46ccf2ec314'
        ),
        value: 'humanoid',
        range: IS_DOMESTIC
          ? generateRange(1, 54)
          : [...generateRange(71, 155), ...generateRange(1, 54)]
      },
      {
        label: 'Robot',
        value: 'robot',
        range: generateRange(55, 58)
      },
      {
        label: t(
          'components.AgentAvatar.picker.f7bb73169d1a7f2e30960270466d6751'
        ),
        value: 'tool',
        range: generateRange(59, 70)
      }
    ];

    const filteredAvatars = computed(() => {
      return tabs.find(avatar => avatar.value === activeTab.value).range;
    });

    const selectAvatar = index => {
      console.log(index, 112);
      currentIcon.value = index;
      currentIconType.value = activeTab.value;
      emit('select', {
        icon: index
      });
    };

    const clickSelectedAvatar = type => {
      activeTab.value = type;
    };

    return {
      currentIcon,
      currentIconType,
      activeTab,
      tabs,
      filteredAvatars,
      selectAvatar,
      clickSelectedAvatar
    };
  }
});
</script>

<style lang="less" scoped>
.avatar-selector {
  width: 552px;
  padding: 12px 8px 12px 16px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0px 18px 56px 16px rgba(0, 0, 0, 0.05),
    0px 6px 12px -8px rgba(0, 0, 0, 0.12), 0px 12px 32px 0px rgba(0, 0, 0, 0.08);
}

.avatar-tabs {
  width: 246px;
  display: flex;
  gap: 4px;
  background: var(--background-neutral-bg-color-layout);
  padding: 2px;
  border-radius: 6px;
}

.avatar-tab {
  flex: 1;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--text-neutral-color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
  padding: 0 8px;

  &:hover {
    background: rgba(89, 89, 235, 0.1);
  }

  &.active {
    background: var(--text-primary-color-primary-text);
    color: var(--text-neutral-color-light-solid);
    font-weight: 600;
  }
}

.avatar-grid-wrap {
  margin-top: 16px;
  padding-right: 8px;
  min-height: 194px;
  max-height: 194px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scrollbar-scrollbar-color-default);
    border-radius: 6px;
    cursor: pointer;
  }
}
.avatar-grid-container {
  display: flex;
  gap: 12px;
  padding-left: 8px;
}

.avatar-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  column-gap: 20px;
  row-gap: 22px;
}

.avatar-item {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  // overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  &.selected {
    border: 2px solid var(--text-primary-color-primary-text);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.selected-badge {
  position: absolute;
  bottom: -1px;
  right: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary-color-primary-text);
}
</style>
